import { CreateSliceOptions, Draft, PayloadAction } from '@reduxjs/toolkit';
import getCheckThunk from './check/getCheckThunk';
import getLoginThunk from './login/getLoginThunk';
import getLogoutThunk from './logout/getLogoutThunk';
import {
  AccountData,
  SessionAPIsI,
  SessionThunksI,
  SetUserDataThunk,
} from './types';

export interface SessionStateI {
  logged: boolean;
  checked: boolean;
  userData: AccountData | null;
}

interface SessionReducers<StateI extends SessionStateI> {
  setLogged: (state: Draft<StateI>, action: PayloadAction<boolean>) => void;
}

export const getInitialState = (): SessionStateI => ({
  logged: false,
  checked: false,
  userData: null,
});

export const getSessionReducers = <
  StateI extends SessionStateI
>(): SessionReducers<StateI> => ({
  setLogged: (state, action: PayloadAction<boolean>): void => {
    state.logged = action.payload;
  },
});

export const getSessionThunks = (
  setUserData: SetUserDataThunk,
  sessionAPIs: SessionAPIsI
): SessionThunksI => ({
  login: getLoginThunk(setUserData, sessionAPIs),
  check: getCheckThunk(setUserData, sessionAPIs),
  logout: getLogoutThunk(setUserData, sessionAPIs),
  setUserData,
});

export const getSessionExtraReducers = <StateI extends SessionStateI>(
  sessionThunks: SessionThunksI
): CreateSliceOptions['extraReducers'] => {
  const { login, check, setUserData } = sessionThunks;

  return {
    [check.fulfilled.type]: (state: StateI): void => {
      state.checked = true;
    },
    [check.rejected.type]: (state: StateI): void => {
      state.checked = true;
      state.logged = false;
      state.userData = null;
    },
    [setUserData.fulfilled.type]: (
      state: StateI,
      action: PayloadAction<AccountData>
    ): void => {
      state.logged = !!action.payload;
      state.userData = action.payload;
    },
    [login.rejected.type]: (state: StateI): void => {
      state.logged = false;
    },
  };
};
